import { observer } from 'mobx-react-lite';
import { IPromiseBasedObservable } from 'mobx-utils';
import { useEffect, useState } from 'react';
import {
  Button,
  CheckboxField,
  DateField,
  PromiseContainer,
  TextField,
} from '../../../../components';
import {
  Dialog,
  DialogActions,
  DialogButton,
  DialogContent,
  ModalInstance,
} from '../../../../components/modals/utils';
import { useAccount, useStore } from '../../../../store';
import { copyToClipboard } from '../../../../utils/browser';
import { onWarning } from '../../../../utils/errors';
import Product from '../../store/Product';

const WEEK = 7 * 24 * 60 * 60 * 1000;
const MONTH = 30 * 24 * 60 * 60 * 1000;

export default function MakeShareableLinksButton({
  product,
}: {
  product: Product;
}) {
  if (product.isNew || product.archived) {
    return null;
  }

  function onClick() {
    showShareableLinkButton({ product }).catch(onWarning);
  }

  return (
    <>
      <Button icon="share-alt" onClick={onClick} type="secondary">
        Make shareable link
      </Button>
      <ShareableLinkDialog />
    </>
  );
}

const dialog = new ModalInstance<ShareOptions>('ShareableLinkDialog');

const ShareableLinkDialog = observer(function ShareableLinkDialog() {
  const [options] = dialog.useOptions();
  const { productStore } = useStore();
  const account = useAccount();
  const [makeLinksPromise, setMakeLinksPromise] =
    useState<IPromiseBasedObservable<string[]>>();
  const { product } = options || {};
  const [expirationDate, setExpirationDate] = useState(
    new Date(Date.now() + WEEK),
  );
  const [useCdn, setUseCdn] = useState(product?.useCdn);
  const areLinksCreated = makeLinksPromise?.state === 'fulfilled';
  const links = (makeLinksPromise?.value as string[]) || [];

  useEffect(() => setMakeLinksPromise(undefined), [options]);
  useEffect(() => setUseCdn(product?.useCdn), [product?.useCdn]);

  const isCdnAvailable = !account.configurations.cdn.isEmpty;

  async function onMakeLinkClick() {
    if (!product) {
      throw new Error('Product is empty');
    }

    setMakeLinksPromise(
      productStore.makeShareableLinks(product, expirationDate, useCdn),
    );
  }

  function onCopyClick() {
    copyToClipboard(links.join('\n'));
  }

  if (!options || !product) {
    return null;
  }

  const secondsUntilExpiry = product.useCdn ? MONTH : WEEK;
  const maxDate = new Date(Date.now() + secondsUntilExpiry);

  return (
    <Dialog
      fullWidth
      onClose={() => dialog.close('cancel')}
      title="Share product files"
    >
      <DialogContent>
        <DateField
          disablePast
          label="Link expiration date"
          maxDate={maxDate}
          onChange={({ value }) => setExpirationDate(value)}
          value={expirationDate}
        />
        {isCdnAvailable && (
          <CheckboxField
            label="Use Enhanced CDN"
            onChange={(e) => setUseCdn(e.target.checked)}
            sx={{ ml: 3, mt: 3 }}
            value={useCdn}
          />
        )}

        <PromiseContainer promise={makeLinksPromise}>
          {(urls) => (
            <TextField
              disabled
              label="Product Links"
              multiline
              rows={5}
              sx={{ mt: 3 }}
              value={urls.join('\n\n')}
              variant="outlined"
            />
          )}
        </PromiseContainer>
      </DialogContent>
      <DialogActions>
        {areLinksCreated && (
          <DialogButton
            icon="copy"
            onClick={onCopyClick}
            title="Copy to clipboard"
          />
        )}
        <DialogButton
          onClick={onMakeLinkClick}
          title={
            areLinksCreated ? 'Make new shareable link' : 'Make shareable link'
          }
          type={areLinksCreated ? 'secondary' : undefined}
        />
        <DialogButton
          onClick={() => dialog.close('cancel')}
          title="Cancel"
          type="secondary"
        />
      </DialogActions>
    </Dialog>
  );
});

function showShareableLinkButton(options: ShareOptions): Promise<string> {
  return dialog.show(options);
}

interface ShareOptions {
  product: Product;
}
